import { useMemo } from 'react';
import { trpc } from '@/api/trpc';
import type { SearchSelectItem } from '@/components/UI/SearchSelect';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import type { TradingProfileDirectory } from '@/pages/User/PortfolioSettings/components/strategySubscription';
import { useAppQuery } from './useAppQuery';
import { useTradingProfiles } from './useTradingProfiles';

/**
 * The user's own trading profiles merged with the profiles shared with them: display names for every
 * profile a subscription can reference, which of those the user owns, and the options a subscription
 * may link (owned or consumable).
 */
export const useTradingProfileDirectory = (options?: { setError?: (message: string) => void }) => {
    const setError = options?.setError;

    const { tradingProfiles, query: tradingProfilesQuery } = useTradingProfiles({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading profiles',
            showErrorToast: true,
            setError
        }
    });

    const accessQuery = useAppQuery(
        {
            ...trpc.resourceAccess.listAccessibleResources.queryOptions({
                resourceType: 'tradingProfile'
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load shared trading profile access',
            showErrorToast: true,
            setError
        }
    );

    const directory = useMemo<TradingProfileDirectory>(() => {
        const tradingProfileDisplayNameById = new Map<number, string>();

        for (const profile of tradingProfiles) {
            tradingProfileDisplayNameById.set(profile.id, profile.name);
        }

        for (const item of accessQuery.data ?? []) {
            tradingProfileDisplayNameById.set(item.resource.resourcePk, item.displayName);
        }

        return {
            ownedTradingProfileIds: new Set(tradingProfiles.map((profile) => profile.id)),
            tradingProfileDisplayNameById
        };
    }, [tradingProfiles, accessQuery.data]);

    const tradingProfileOptions = useMemo<SearchSelectItem[]>(() => {
        const optionsById = new Map<number, string>();

        for (const profile of tradingProfiles) {
            optionsById.set(profile.id, profile.name);
        }

        for (const item of accessQuery.data ?? []) {
            if (item.canConsume) {
                optionsById.set(item.resource.resourcePk, item.displayName);
            }
        }

        return [...optionsById.entries()]
            .sort((first, second) => first[1].localeCompare(second[1]))
            .map(([profileId, profileName]) => ({
                value: profileId,
                label: profileName,
                name: profileName
            }));
    }, [tradingProfiles, accessQuery.data]);

    return {
        directory,
        /** Both the owned profiles and the shared-access list have loaded. */
        isLoaded: tradingProfilesQuery.status === 'success' && accessQuery.status === 'success',
        tradingProfileOptions
    };
};
